import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useEffect} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {AxiosError} from "axios";

import {Button} from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {useToast} from "@/src/modules/core/hooks";
import {useStoredMe} from "@/src/modules/core/store/me";

import useCompletePlaceholderProfile from "../hooks/use-complete-placeholder-profile";
import {meRequiresPlaceholderProfileCompletion} from "../lib/placeholder-profile";
import {completePlaceholderProfileSchema} from "../schemas";
import {useSessionStore} from "../store/session";

export function CompletePlaceholderProfileModal() {
  const toast = useToast();
  const me = useStoredMe();
  const session = useSessionStore((state) => state.session);
  const {mutateAsync: completeProfile, isPending} = useCompletePlaceholderProfile();

  const form = useForm<z.infer<typeof completePlaceholderProfileSchema>>({
    defaultValues: {
      first_name: "",
      last_name: "",
      telephone: "",
    },
    resolver: zodResolver(completePlaceholderProfileSchema),
  });

  useEffect(() => {
    if (!me) {
      return;
    }

    form.reset({
      first_name: me.first_name ?? "",
      last_name: me.last_name ?? "",
      telephone: me.telephone?.trim() ?? "",
    });
  }, [form, me?.first_name, me?.last_name, me?.telephone, me?.user_id]);

  const mustCompleteProfile =
    meRequiresPlaceholderProfileCompletion(me) && session?.requires_password_change !== true;

  const handleSubmit = async (data: z.infer<typeof completePlaceholderProfileSchema>) => {
    try {
      await completeProfile({
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        telephone: data.telephone?.trim() || undefined,
      });
    } catch (error) {
      let errorMessage = "Could not update your profile";

      if (error instanceof AxiosError) {
        const responseData = error.response?.data as
          | {message?: string; errors?: Record<string, string[]>}
          | undefined;

        if (responseData?.message) {
          errorMessage = responseData.message;
        }

        const firstFieldError = responseData?.errors
          ? Object.values(responseData.errors).flat().find(Boolean)
          : undefined;

        if (firstFieldError) {
          errorMessage = firstFieldError;
        }
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={mustCompleteProfile} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl">Complete your profile</DialogTitle>
          <DialogDescription className="text-center">
            Enter your name before using the app. Phone is optional.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="first_name"
                render={({field, formState}) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isPending} autoComplete="given-name" />
                    </FormControl>
                    {formState.errors.first_name ? (
                      <FormError error={formState.errors.first_name.message} />
                    ) : null}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({field, formState}) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isPending} autoComplete="family-name" />
                    </FormControl>
                    {formState.errors.last_name ? (
                      <FormError error={formState.errors.last_name.message} />
                    ) : null}
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="telephone"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>Phone (optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      disabled={isPending}
                      autoComplete="tel"
                      placeholder="(555) 123-4567"
                    />
                  </FormControl>
                  {formState.errors.telephone ? (
                    <FormError error={formState.errors.telephone.message} />
                  ) : null}
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  Saving...
                  <Loader2 className="size-4 animate-spin" />
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
